const GENERIC_FAILURE_MESSAGE =
  'Video processing failed on the server. Please try uploading again.';

const RE_UPLOAD_WONT_HELP_HINT = "Re-uploading the same file won't fix this — re-export it first.";

const NON_RECOVERABLE_PATTERN = /transcod|codec|profile|encoding|chroma|pixel format|bit depth/i;
const INVALID_FILE_PATTERN = /invalid file|cannot load file/i;

export type ProcessingFailureReason =
  | 'bunny_codec_unsupported'
  | 'bunny_invalid_file'
  | 'bunny_unknown'
  | 'reaper_bunny_no_record'
  | 'reaper_no_bytes'
  | 'reaper_partial_bytes'
  | 'reaper_failed_upstream';

export interface ProcessingFailure {
  /** Stable enum for telemetry + tests; never shown to creators. */
  reason: ProcessingFailureReason;
  message: string;
  hint?: string;
}

/**
 * The BO reaper (`ReconcileStuckBunnyVideos`) marks stuck rows failed through
 * `applyFailedState($video, 'reaper:<outcome>')`; when Bunny has no encoder
 * log for the row that literal lands in `bunny_last_error` verbatim. These
 * are the only non-Bunny strings the field can carry.
 */
const REAPER_FAILURES: Readonly<
  Record<string, { reason: ProcessingFailureReason; message: string }>
> = {
  'reaper:bunny_no_record': {
    reason: 'reaper_bunny_no_record',
    message: 'Our video host has no record of this upload. Upload the file again.',
  },
  'reaper:no_bytes': {
    reason: 'reaper_no_bytes',
    message:
      'The upload never delivered any data — the connection dropped before the file started. Upload the file again.',
  },
  'reaper:partial_bytes': {
    reason: 'reaper_partial_bytes',
    message: 'The upload stopped partway through and never finished. Upload the file again.',
  },
  'reaper:failed_upstream': {
    reason: 'reaper_failed_upstream',
    message: 'Our video host could not process this file. Re-export it and upload again.',
  },
};

/**
 * Classify a `bunny_last_error` string into a creator-facing failure.
 *
 * Order: reaper literals (exact) → codec/profile patterns (the Bunny string
 * is the most useful copy, plus a "re-export first" hint) → invalid-file
 * (Bunny downloaded something that isn't a video) → any other Bunny string
 * verbatim → generic fallback when null.
 */
export function describeProcessingFailure(
  bunnyLastError: string | null | undefined
): ProcessingFailure {
  if (!bunnyLastError) {
    return { reason: 'bunny_unknown', message: GENERIC_FAILURE_MESSAGE };
  }
  const reaper = REAPER_FAILURES[bunnyLastError.trim()];
  if (reaper) return reaper;
  if (NON_RECOVERABLE_PATTERN.test(bunnyLastError)) {
    return {
      reason: 'bunny_codec_unsupported',
      message: bunnyLastError,
      hint: RE_UPLOAD_WONT_HELP_HINT,
    };
  }
  // No hint: Bunny's string here is a temp path, not something a creator can act on.
  if (INVALID_FILE_PATTERN.test(bunnyLastError)) {
    return {
      reason: 'bunny_invalid_file',
      message:
        "Our video host couldn't read this file. Make sure it's an actual video file — not a web page, archive or document.",
    };
  }
  return { reason: 'bunny_unknown', message: bunnyLastError };
}

export interface BunnyFailureCopy {
  primary: string;
  hint?: string;
}

/** Presentation shape kept for existing row consumers (courses lesson rows, content table). */
export function formatBunnyFailureCopy(
  bunnyLastError: string | null | undefined
): BunnyFailureCopy {
  const { message, hint } = describeProcessingFailure(bunnyLastError);
  return hint === undefined ? { primary: message } : { primary: message, hint };
}

export function getBunnyFailureMessage(bunnyLastError: string | null | undefined): string {
  return describeProcessingFailure(bunnyLastError).message;
}
